import { Scissors } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { usePlan } from '@/app/providers';
import { useMasterSession } from '@/features/auth/hooks/useSession';
import { ROUTES } from '@/shared/config/routes';
import { cn, initialsOf } from '@/shared/lib';

import { isNavItemActive, SIDEBAR_LINKS } from './dashboardNav';

/**
 * Plan chip under the master's name. Free gets a quiet chip rather than none:
 * without it the line read "Plan:" followed by nothing.
 */
const PlanBadge = () => {
  const { t } = useTranslation('app');
  const { tier } = usePlan();

  return (
    <span
      className={cn(
        'rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wider uppercase',
        tier === 'free' && 'bg-gray-100 text-gray-500',
        tier === 'studio' && 'bg-purple-50 text-purple-600',
        (tier === 'solo' || tier === 'pro') && 'bg-primary/10 text-primary',
      )}
    >
      {t(`common:plans.${tier}`)}
    </span>
  );
};

/** Fixed left navigation on the master dashboard. Desktop only. */
export const DashboardSidebar = () => {
  const { t } = useTranslation(['app', 'common']);
  const name = useMasterSession().user?.name ?? '';
  const { pathname } = useLocation();
  const { tier } = usePlan();

  return (
    <aside className="fixed inset-y-0 z-40 hidden w-[260px] flex-col border-r border-gray-100 bg-white md:flex">
      <div className="p-6">
        <Link to={ROUTES.home} className="flex items-center gap-2 text-primary">
          <div className="rounded-xl bg-primary/10 p-2">
            <Scissors size={24} className="text-primary" />
          </div>
          <span className="font-heading text-2xl font-bold tracking-tight text-dark">
            {t('common:brand')}
          </span>
        </Link>
      </div>

      <nav className="flex-1 space-y-2 px-4 py-4">
        {SIDEBAR_LINKS.map(({ icon: Icon, labelKey, path, badge }) => {
          const active = isNavItemActive(pathname, path);
          return (
            <Link
              key={path}
              to={path}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'flex items-center justify-between gap-3 rounded-xl px-4 py-3 font-medium transition-colors',
                active
                  ? 'bg-primary text-white shadow-md shadow-primary/20'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-dark',
              )}
            >
              <div className="flex items-center gap-3">
                <Icon size={20} />
                {t(labelKey)}
              </div>
              {badge && tier !== 'studio' && (
                <span
                  className={cn(
                    'rounded px-1.5 py-0.5 text-[9px] font-bold tracking-wider uppercase',
                    active ? 'bg-white/20 text-white' : 'bg-primary/10 text-primary',
                  )}
                >
                  {badge}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      <div className="flex flex-col gap-2 border-t border-gray-100 p-4">
        <div className="flex items-center gap-3 rounded-xl p-3">
          <span
            aria-hidden="true"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-white"
          >
            {initialsOf(name)}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-dark">{name}</p>
            <div className="mt-0.5 flex items-center">
              <span className="mr-1 truncate text-xs text-gray-500">{t('sidebar.planLabel')}</span>
              <PlanBadge />
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
};
